import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../constants';
import { Mail, Loader2, CheckCircle2 } from 'lucide-react';

import { t } from '@/i18n';
/**
 * Shared submit hook — POST /api/newsletter/subscribe and manage the
 * success / error / loading tri-state so every entry point (inline
 * blog card, timed popup, sticky bar) has consistent behavior.
 *
 * `source` lets the admin trace which surface converted best (signup
 * from popup vs sticky vs blog).
 */
export function useNewsletterSubscribe(source = 'signup') {
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [error, setError] = useState('');

  const submit = async ({ email, first_name }) => {
    const clean = (email || '').trim();
    if (!clean || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(clean)) {
      setError('Enter a valid email address');
      setStatus('error');
      return { ok: false };
    }
    setStatus('submitting');
    setError('');
    try {
      const r = await axios.post(`${API}/newsletter/subscribe`, {
        email: clean,
        first_name: (first_name || '').trim() || undefined,
        source,
      });
      setStatus('success');
      // localStorage flag so the popup / sticky bar don't re-nag
      try { localStorage.setItem('aan_newsletter_signup', '1'); } catch { /* ignore */ }
      return { ok: true, already: !!r.data.already_subscribed };
    } catch (e) {
      const msg = e.response?.data?.detail || 'Could not subscribe. Try again in a moment.';
      setError(msg);
      setStatus('error');
      toast.error(msg);
      return { ok: false, error: msg };
    }
  };

  const reset = () => { setStatus('idle'); setError(''); };

  return { status, error, submit, reset };
}


/**
 * Inline hero-style signup card — meant to live on the Blog index page.
 * Compact, on-brand, always visible above the fold.
 */
export const NewsletterInlineCard = ({ eyebrow = "Monthly newsletter" }) => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const { status, error, submit } = useNewsletterSubscribe('blog_inline');

  const disabled = status === 'submitting' || status === 'success';

  const onSubmit = async (e) => {
    e.preventDefault();
    await submit({ email, first_name: firstName });
  };

  return (
    <section
      className="rounded-2xl overflow-hidden my-10"
      style={{
        background: 'linear-gradient(135deg, #0B1220 0%, #0F172A 55%, #1E293B 100%)',
      }}
      data-testid="newsletter-inline-card"
    >
      <div className="grid grid-cols-1 md:grid-cols-5 gap-0 items-center">
        <div className="md:col-span-3 p-8 md:p-10">
          <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.24em] text-[#F97316] mb-3">
            <Mail className="h-3.5 w-3.5" /> {eyebrow}
          </div>
          <h3 className="text-2xl md:text-3xl font-bold text-white leading-tight mb-3">
            {t("The construction lien playbook, in your inbox once a month.")}
          </h3>
          <p className="text-slate-300 text-sm md:text-base leading-relaxed max-w-md">
            {t("State deadline changes, notice pitfalls, and the plays that get contractors paid — from the desk that files thousands of preliminary notices every month.")}
          </p>
        </div>

        <div className="md:col-span-2 p-6 md:p-8 md:pl-2">
          {status === 'success' ? (
            <div className="bg-white/5 border border-emerald-500/30 rounded-xl p-5 text-center">
              <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto mb-2" />
              <div className="text-white font-semibold">{t("You're on the list.")}</div>
              <div className="text-slate-400 text-sm mt-1">
                {t("First newsletter drops soon. No spam, unsubscribe any time.")}
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-2.5" data-testid="newsletter-inline-form">
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name (optional)"
                className="w-full rounded-lg bg-white/10 border border-white/15 px-4 py-3 text-white placeholder:text-slate-400 focus:outline-none focus:border-[#F97316]"
                data-testid="newsletter-inline-first-name"
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@company.com"
                className="w-full rounded-lg bg-white/10 border border-white/15 px-4 py-3 text-white placeholder:text-slate-400 focus:outline-none focus:border-[#F97316]"
                data-testid="newsletter-inline-email"
              />
              <button
                type="submit"
                disabled={disabled}
                className="w-full rounded-lg py-3 font-bold text-[#0F172A] uppercase tracking-[0.06em] text-sm transition-opacity disabled:opacity-60"
                style={{ background: '#F97316' }}
                data-testid="newsletter-inline-submit"
              >
                {status === 'submitting' ? (
                  <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> {t("Subscribing...")}</span>
                ) : t("Subscribe free →")}
              </button>
              {error && (
                <div className="text-xs text-red-300 pt-1" data-testid="newsletter-inline-error">{error}</div>
              )}
              <div className="text-[11px] text-slate-500 pt-1">{t("No spam. One click to unsubscribe.")}</div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default NewsletterInlineCard;
